/**
 * Presentation helpers for the workspace switcher.
 *
 * The mock roster that used to live here is gone — workspaces are real, and the
 * switcher reads them from `TenantContext`. What is left is the two purely
 * cosmetic fields the design calls for and the API deliberately does not carry:
 * a monogram and an accent. Both are derived from the tenant, so a workspace
 * looks the same on every device without storing a colour nobody chose.
 */

import type { TenantMembershipSummary } from '@app/common';

/** Tailwind gradients for the monogram tile, picked by hashing the tenant id. */
const ACCENT_CLASSES = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-violet-500 to-purple-600',
  'from-rose-500 to-pink-600',
  'from-cyan-500 to-sky-600',
] as const;

/** Two letters: the initials of the first two words, or the first two letters. */
export function workspaceInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Stable per tenant, so the same workspace keeps its colour across sessions and
 * across users. Hashing the id rather than the name means renaming a workspace
 * does not recolour it.
 */
export function workspaceAccentClass(tenantId: string): string {
  let hash = 0;
  for (let i = 0; i < tenantId.length; i += 1) {
    hash = (hash * 31 + (tenantId.codePointAt(i) ?? 0)) >>> 0;
  }
  return ACCENT_CLASSES[hash % ACCENT_CLASSES.length];
}

export type WorkspaceMembership = TenantMembershipSummary;
